
import React from 'react';
import { allParksAndFacilities } from '../../../mocks/parks';

interface ParksGridProps {
  category: string;
}

export default function ParksGrid({ category }: ParksGridProps) {
  // Guard against unexpected data shapes
  const filtered = Array.isArray(allParksAndFacilities)
    ? allParksAndFacilities.filter((item) => item.type === category)
    : [];

  if (filtered.length === 0) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <i className="ri-search-line text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-500">No locations found for this category.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="all" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">
          {category}{' '}
          <span className="text-gray-400 font-normal text-lg">
            ({filtered.length})
          </span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                    {item.type}
                  </span>
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-800">
                    {item.rating}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">
                  {item.name}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                  {item.description}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {Array.isArray(item.amenities) &&
                    item.amenities.slice(0, 4).map((a) => (
                      <span
                        key={a}
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                      >
                        {a}
                      </span>
                    ))}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <i className="ri-time-line"></i>
                    <span>{item.hours}</span>
                  </div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      item.address
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-emerald-600 hover:text-emerald-700 text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    Directions <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
